/**
 * "See it work": the loop of the four parts, and three hands-on views under it.
 *
 * ## THE LOOP
 *
 * The concept's six boxes (four parts on top, People and Audiences beneath),
 * drawn by @wend/ui's `PillarLoop` in interactive mode: press a part to light
 * what it hands on and read one short panel of what it receives and hands on.
 * The concept's two arcs back into Reach (Sense moving ad budget, converted
 * people sent to Meta) are absent because neither is built; Flow → Connect is
 * drawn because every flow message is a Connect send. Every edge is solid —
 * each is data that moves today. Below 1024px the map becomes a column of the
 * same six buttons (PillarLoop's `collapse="list"`).
 *
 * ## THREE TABS: BUILD A SEGMENT · RUN A FLOW · FOLLOW ONE PERSON
 *
 * The owner asked for a page that shows things in action. The two demos and
 * the stepper are the most "in action" things on it, so they sit together
 * under the loop as tabs, each a working view over example data: the segment
 * builder (SegmentDemo), the flow canvas with Simulate (FlowDemo), and Priya's
 * story (PersonJourney), which lights the loop's edge at every step.
 *
 * The tabs are the WAI-ARIA pattern: real buttons with role="tab"; arrow keys
 * and Home/End move between them (roving tabindex, `tabForKey` is pure and the
 * suite tests it). Every panel stays mounted with `hidden` on the inactive
 * ones, so a demo keeps its state when the reader looks away and comes back.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';

import { PillarLoop, cn, type LoopEdge, type LoopNode, type LoopNodeState } from '@wend/ui';

import { PersonJourney } from '../components/PersonJourney';
import { Eyebrow, Section } from '../components/site-chrome';
import { JOURNEY, firstStepOf } from '../lib/journey';
import { PARTS, PART_ORDER, nameOf, type PartId, type PartTone, type TryTab } from '../lib/parts';
import { FlowDemo } from './FlowDemo';
import { SegmentDemo } from './SegmentDemo';

const VIEW = { w: 1000, h: 470 } as const;

const BOX: Readonly<Record<PartId, LoopNode['box']>> = {
  reach: { x: 20, y: 100, w: 170, h: 120 },
  connect: { x: 276, y: 100, w: 170, h: 120 },
  sense: { x: 554, y: 100, w: 170, h: 120 },
  flow: { x: 810, y: 100, w: 170, h: 120 },
  people: { x: 20, y: 330, w: 460, h: 104 },
  audiences: { x: 520, y: 330, w: 460, h: 104 },
};

/* The four parts share one look (the brand kicker); the two foundations are muted. No status is drawn. */
const TONE_STATE: Readonly<Record<PartTone, LoopNodeState>> = {
  part: 'available',
  foundation: 'foundation',
};

const NODES: readonly LoopNode[] = PART_ORDER.map((id) => {
  const part = PARTS[id];
  return { id, kicker: part.kicker, name: part.name, line: part.line, glyph: part.glyph, state: TONE_STATE[part.tone], box: BOX[id] };
});

/** Edge ids are `from>to`; `lib/journey.tsx` names them per step. */
export const LOOP_EDGES: readonly LoopEdge[] = [
  { id: 'reach>people', from: 'reach', to: 'people', style: 'solid', d: 'M 105 220 L 105 326', label: { text: 'lead · ad_id', x: 105, y: 275 } },
  { id: 'people>audiences', from: 'people', to: 'audiences', style: 'solid', d: 'M 482 382 L 516 382', label: { text: 'every column · derived fields', x: 500, y: 452 } },
  { id: 'audiences>connect', from: 'audiences', to: 'connect', style: 'solid', d: 'M 570 330 C 570 272, 361 282, 361 224', label: { text: 'segment members', x: 462, y: 276 } },
  { id: 'audiences>flow', from: 'audiences', to: 'flow', style: 'solid', d: 'M 895 330 L 895 224', label: { text: 'entered / left', x: 895, y: 276 } },
  { id: 'connect>sense', from: 'connect', to: 'sense', style: 'solid', d: 'M 446 146 L 550 146', label: { text: 'outcomes', x: 498, y: 146 } },
  { id: 'sense>connect', from: 'sense', to: 'connect', style: 'solid', d: 'M 554 180 L 450 180', label: { text: 'suggestions', x: 502, y: 180 } },
  { id: 'sense>flow', from: 'sense', to: 'flow', style: 'solid', d: 'M 724 143 L 806 143', label: { text: 'draft', x: 765, y: 143 } },
  { id: 'flow>sense', from: 'flow', to: 'sense', style: 'solid', d: 'M 810 181 L 728 181', label: { text: 'results', x: 769, y: 181 } },
  { id: 'flow>connect', from: 'flow', to: 'connect', style: 'solid', d: 'M 895 100 C 895 40, 361 40, 361 96', label: { text: 'each message · same checks', x: 628, y: 55 } },
];

const chipButton = cn(
  'inline-flex items-center gap-1.5 rounded-full border border-brand-b/50 bg-brand-a/24 px-3.5 py-1.5 text-[0.8rem] text-brand-ink-strong transition-colors hover:bg-brand-a/36',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b',
);
const chipGhost = cn(
  'inline-flex items-center gap-1.5 rounded-full border border-line-strong px-3.5 py-1.5 text-[0.8rem] text-muted transition-colors hover:bg-white/5 hover:text-text',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b',
);

function FlowList({ heading, dot, lines, prefix, nothing }: {
  heading: string;
  dot: string;
  lines: readonly { other: string; what: string }[];
  prefix: 'from' | 'to';
  nothing?: string;
}) {
  return (
    <div>
      <h4 className="mb-2 flex items-center gap-2 font-mono text-[0.66rem] uppercase tracking-[0.13em] text-muted">
        <span aria-hidden="true" className={cn('size-2 rounded-full', dot)} />
        {heading}
      </h4>
      {lines.length === 0 ? (
        <p className="rounded-[10px] border border-dashed border-line-strong px-3 py-2.5 text-[0.86rem] leading-relaxed text-muted">{nothing}</p>
      ) : (
        <ul className="grid gap-2">
          {lines.map((line) => (
            <li key={line.other + line.what} className="rounded-[10px] border border-line bg-white/[0.035] px-3 py-2 text-[0.86rem] leading-normal text-text">
              <span className="mb-0.5 block font-mono text-[0.64rem] uppercase tracking-[0.06em] text-brand-ink">
                {prefix} {nameOf(line.other)}
              </span>
              {line.what}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** What the selected part receives and hands on — the loop's live detail panel. Exported for the site suite. */
export function PartDetail({ id, onJump, onTry }: { id: PartId; onJump?: (id: PartId) => void; onTry?: (tab: TryTab) => void }) {
  const part = PARTS[id];
  const storyStep = firstStepOf(part.id);
  const tryIt = part.tryIt;
  return (
    <div className="rounded-lg border border-brand-b/30 bg-linear-to-b from-brand-a/10 to-brand-a/2 px-5 py-5 sm:px-6">
      <div className="flex flex-wrap items-center gap-3.5">
        <span aria-hidden="true" className="grid size-[42px] flex-none place-items-center rounded-[12px] bg-brand-gradient text-[1.1rem] text-white shadow-brand">
          {part.glyph}
        </span>
        <div>
          <p className="font-mono text-[0.62rem] uppercase tracking-[0.13em] text-brand-ink">{part.kicker} · selected</p>
          <h3 className="text-xl font-semibold tracking-tight text-text">{part.name}</h3>
        </div>
        <div className="flex flex-wrap gap-2 sm:ml-auto">
          {storyStep !== null && onJump !== undefined && (
            <button type="button" className={chipButton} onClick={() => onJump(part.id)}>
              See it in Priya’s story · step {storyStep + 1}
            </button>
          )}
          {tryIt !== undefined && onTry !== undefined && (
            <button type="button" className={chipGhost} onClick={() => onTry(tryIt.tab)}>
              {tryIt.label} <span aria-hidden="true">↓</span>
            </button>
          )}
        </div>
      </div>
      <div className="mt-4 grid gap-[18px] md:grid-cols-2 md:gap-[26px]">
        <FlowList heading="Receives" dot="bg-text" lines={part.receives} prefix="from" nothing={part.receivesNothing} />
        <FlowList heading="Hands on" dot="bg-brand-b" lines={part.hands} prefix="to" />
      </div>
    </div>
  );
}

export const TRY_TABS: readonly { readonly id: TryTab; readonly label: string }[] = [
  { id: 'segment', label: 'Build a segment' },
  { id: 'flow', label: 'Run a flow' },
  { id: 'story', label: 'Follow one person' },
];

/** The tab a key moves to from `current` (wrapping), or null for a key the tab list leaves alone. Pure, for the suite. */
export function tabForKey(current: TryTab, key: string): TryTab | null {
  const at = TRY_TABS.findIndex((tab) => tab.id === current);
  const last = TRY_TABS.length - 1;
  let next: number | null = null;
  if (key === 'ArrowRight') next = at === last ? 0 : at + 1;
  else if (key === 'ArrowLeft') next = at <= 0 ? last : at - 1;
  else if (key === 'Home') next = 0;
  else if (key === 'End') next = last;
  return next === null ? null : (TRY_TABS[next]?.id ?? null);
}

const reduceMotion = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

export function Loop({ initialTab = 'segment' }: { readonly initialTab?: TryTab } = {}) {
  const [selected, setSelected] = useState<PartId>('reach');
  const [step, setStep] = useState(0);
  const [activeEdge, setActiveEdge] = useState<string | undefined>(undefined);
  const [tab, setTab] = useState<TryTab>(initialTab);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);

  // A link to #try-segment, #try-flow or #try-story opens that tab (and the browser scrolls to it).
  useEffect(() => {
    const fromHash = (): void => {
      const wanted = window.location.hash.replace('#try-', '');
      if (TRY_TABS.some((item) => item.id === wanted)) setTab(wanted as TryTab);
    };
    fromHash();
    window.addEventListener('hashchange', fromHash);
    return () => window.removeEventListener('hashchange', fromHash);
  }, []);

  const select = (id: string): void => {
    if (id in PARTS) {
      setSelected(id as PartId);
      setActiveEdge(undefined);
    }
  };

  const goToStep = (next: number): void => {
    const target = JOURNEY[next];
    if (target === undefined) return;
    setStep(next);
    setSelected(target.part);
    setActiveEdge(target.edge);
  };

  /** Show a tab; from the keyboard, focus it; from the loop's panel, scroll the tabs into view. */
  const openTab = (next: TryTab, from: 'keys' | 'panel'): void => {
    setTab(next);
    requestAnimationFrame(() => {
      if (from === 'keys') {
        tabsRef.current?.querySelector<HTMLButtonElement>(`[data-tab="${next}"]`)?.focus();
      } else {
        // An explicit 'smooth' overrides index.css's reduced-motion gate, so ask.
        tabsRef.current?.scrollIntoView({ block: 'start', behavior: reduceMotion() ? 'auto' : 'smooth' });
      }
    });
  };

  const jump = (id: PartId): void => {
    const at = firstStepOf(id);
    if (at === null) return;
    goToStep(at);
    setTab('story');
    requestAnimationFrame(() => {
      titleRef.current?.scrollIntoView({ block: 'center', behavior: reduceMotion() ? 'auto' : 'smooth' });
      titleRef.current?.focus({ preventScroll: true });
    });
  };

  const onTabKey = (event: KeyboardEvent<HTMLDivElement>): void => {
    const next = tabForKey(tab, event.key);
    if (next === null) return;
    event.preventDefault();
    openTab(next, 'keys');
  };

  const renderDetail = (id: string) =>
    id in PARTS ? <PartDetail id={id as PartId} onJump={jump} onTry={(next) => openTab(next, 'panel')} /> : null;

  return (
    <Section id="loop">
      <div className="reveal text-center">
        <Eyebrow>See it work</Eyebrow>
        <h2 className="mx-auto mt-4 max-w-3xl text-balance text-3xl font-semibold tracking-tight text-text sm:text-5xl">
          Four parts, one loop.
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-pretty text-lg text-muted">Press a part. Then try it yourself.</p>
      </div>

      <div className="mt-12">
        <PillarLoop
          mode="interactive"
          emphasis="direction"
          label="The four parts and their foundations. Press one to see what it receives and hands on"
          idPrefix="site-loop"
          collapse="list"
          viewBox={VIEW}
          nodes={NODES}
          edges={LOOP_EDGES}
          selected={selected}
          onSelect={select}
          activeEdge={activeEdge}
          renderDetail={renderDetail}
        />
      </div>

      <div ref={tabsRef} id="try" className="mt-20 scroll-mt-24">
        <div className="flex flex-col items-center gap-3">
          <Eyebrow>Try it</Eyebrow>
          <div
            role="tablist"
            aria-label="Try Wend"
            onKeyDown={onTabKey}
            className="inline-flex max-w-full flex-wrap justify-center gap-1 rounded-[22px] border border-line-strong bg-surface-1 p-1"
          >
            {TRY_TABS.map((item) => {
              const on = item.id === tab;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  id={`try-tab-${item.id}`}
                  data-tab={item.id}
                  aria-selected={on}
                  aria-controls={`try-panel-${item.id}`}
                  tabIndex={on ? 0 : -1}
                  onClick={() => setTab(item.id)}
                  className={cn(
                    'rounded-full px-4 py-2 text-[0.9rem] transition-colors',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b',
                    on ? 'bg-brand-gradient text-white shadow-brand' : 'text-muted hover:text-text',
                  )}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-8">
          <div role="tabpanel" id="try-panel-segment" aria-labelledby="try-tab-segment" hidden={tab !== 'segment'}>
            <SegmentDemo />
          </div>
          <div role="tabpanel" id="try-panel-flow" aria-labelledby="try-tab-flow" hidden={tab !== 'flow'}>
            <FlowDemo active={tab === 'flow'} />
          </div>
          <div role="tabpanel" id="try-panel-story" aria-labelledby="try-tab-story" hidden={tab !== 'story'}>
            <PersonJourney step={step} onStep={goToStep} titleRef={titleRef} />
          </div>
        </div>
      </div>
    </Section>
  );
}
